<template>
  <p v-if="canSyounin">{{ syouninData.date }}の承認状況を変更しますか？</p>
  <p v-else>{{ syouninData.date }}の承認状況は以下のとおりです<br>（確認者のため閲覧のみとなります）</p>
  <p class="current-status">承認ステータス：<span>{{ currentStatus }}</span></p>
  <p class="current-notes pre" v-bind:class="{ 'has-notes': hasNotes }">コメント：<span>{{ currentNotes }}</span></p>

  <label class="notes" v-if="canSyounin">コメント
    <textarea v-model="syouninNotes" :class="{ error: syouninError }" :disabled="isLoading"></textarea>
    <p class="input-error" v-if="syouninError" style="margin-bottom: 10px;">{{ syouninError }}</p>
  </label>

  <template v-if="isLoading">
    <loading :isLoading="isLoading" :message="'処理中...'"></loading>
  </template>
  <template v-else>
    <div class="buttons status-select" v-if="canSyounin">
      <button type="button" class="btn success" v-on:click="send(OptionConst.DAKOKU_SYOUNIN_STATUS.APPROVED)"><i
          class="fa-solid fa-inbox"></i>承認</button>
      <button type="button" class="btn danger" v-on:click="send(OptionConst.DAKOKU_SYOUNIN_STATUS.DENIED)"><i
          class="fa-solid fa-inbox"></i>否認</button>
    </div>
    <div class="buttons close">
      <button type="button" class="btn cancel" v-on:click="cancel()"><i class="fa-solid fa-x"></i>閉じる</button>
    </div>
  </template>
</template>

<script lang="ts">
import { DakokuSyouninData } from '@/composable/modal/dakoku-syounin';
import OptionConst from '@/const/Option';
import { Ref, computed, defineComponent, reactive, ref } from 'vue';
import { useDakoku } from '@/composable/dakoku/dakoku';
import { DialogService } from '@/service/DialogService';
import { SweetAlertResult } from 'sweetalert2';
import Message from '@/const/Message';
import { boolRes } from '@/types/Dakoku';
import { useForm } from '@/composable/form/form';

export default defineComponent({
  props: {
    syouninData: {
      type: Object as () => DakokuSyouninData,
      default: null
    },    
    canSyounin: {
      type: Boolean,
      default: false
    }
  },
  emits: {
    send: (result: boolean, dakokuId: number, syouinStatus: number, comment: string) => true,
    cancel: () => true,
  },
  setup(props, context) {
    const { dakokuSyounin, isLoading } = useDakoku()
    const { getError } = useForm()
    const syouninNotes = ref('');
    const resultForm:Ref<boolRes> = ref({ result: false })
    const syouninError = computed(() => {
      return getError(resultForm.value, 'syouninStatusNotes')
    })
    const send = async (syouinStatus: number) => {
      if (!syouninData.value) {
        return
      }

      if(!await confirm()){
        return
      }

      try {
        let result = await dakokuSyounin(syouninData.value.dakokuId!, syouinStatus, syouninNotes.value)
        if (!result.result) {
          resultForm.value = result
          throw new Error("")
        }

        let dakokuId = syouninData.value.dakokuId;
        if (dakokuId == null) {
          throw new Error("");
        }
        // 処理結果、打刻ＩＤを親へ渡す
        context.emit('send', true, dakokuId, syouinStatus, syouninNotes.value)
      } catch (e: any) {
        console.log(e)
      }
    }

    const confirm = async () => {
      if (syouninData.value.status !== OptionConst.DAKOKU_SYOUNIN_STATUS.WAIT && syouninData.value.status !== null) {
        return await DialogService.question({
          text: Message.CONFIRM_DAKOKU_SYOUNIN_FOR_ALREADY,
          confirmButtonText: 'はい',
          showCancelButton: true,
          cancelButtonText: 'いいえ',
        }).then(async (result: SweetAlertResult) => {
          return result.isConfirmed
        })
      }
      return true
    }

    const cancel = () => {
      context.emit('cancel');
    }

    const syouninData = computed(() => {
      return props.syouninData
    })

    const currentStatus = computed(() => {
      const status = OptionConst.DAKOKU_SYOUNIN_STATUS_OPTION.find(opt => {
        return opt.val === props.syouninData.status
      })

      return status ? status.label : '-'
    })

    const currentNotes = computed(() => {
      return props.syouninData.syouninNotes ? props.syouninData.syouninNotes : '-'
    })

    const hasNotes = computed(() => {
      return props.syouninData.syouninNotes ? true : false
    })

    return {
      send,
      cancel,
      currentStatus,
      syouninNotes,
      currentNotes,
      hasNotes,
      OptionConst,
      syouninError,
      isLoading,
    }
  }
})
</script>

<style lang="scss" scoped>
@import '@scss/dialog/dakoku-syounin.scss';
</style>
